import * as React from 'react';
import { Check, Minus } from 'lucide-react';

import { cn } from '@/lib/utils';

// Themed checkbox + radio — the canonical selection controls for the app.
// A real, focusable <input> is visually hidden (sr-only) for accessibility and
// form semantics, while a custom box is drawn with the --color-* token family so
// the control tracks the active tenant theme (light, dark, and custom palettes)
// instead of the un-themed shadcn `border-input`/`ring-ring` tokens.
//
// Prefer these over raw <input type="checkbox|radio"> in product UI. The whole
// row (box + label + description) is the click target, and the box is top-aligned
// with the first line of the label so multi-line labels stay tidy.

type BaseProps = Omit<
  React.ComponentProps<'input'>,
  'type' | 'onChange' | 'children'
> & {
  /** Inline label rendered beside the box; row becomes the click target. */
  label?: React.ReactNode;
  /** Secondary line under the label (muted). */
  description?: React.ReactNode;
  /** Fired with the next checked state (in addition to native onChange). */
  onCheckedChange?: (checked: boolean) => void;
  /** Native onChange still available for callers that want the event. */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  /** Extra classes for the outer wrapper. */
  className?: string;
  /** Extra classes for the visual box. */
  boxClassName?: string;
};

interface CheckboxProps extends BaseProps {
  /** Tri-state: render a dash and set the DOM indeterminate flag. */
  indeterminate?: boolean;
}

const SR_ONLY =
  'sr-only peer absolute inset-0 m-0 h-full w-full cursor-pointer disabled:cursor-not-allowed';

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      description,
      indeterminate,
      onCheckedChange,
      onChange,
      className,
      boxClassName,
      disabled,
      checked,
      ...props
    },
    forwardedRef,
  ) => {
    const innerRef = React.useRef<HTMLInputElement | null>(null);
    const setRefs = (el: HTMLInputElement | null) => {
      innerRef.current = el;
      if (typeof forwardedRef === 'function') forwardedRef(el);
      else if (forwardedRef) forwardedRef.current = el;
    };

    React.useEffect(() => {
      if (innerRef.current) innerRef.current.indeterminate = Boolean(indeterminate);
    }, [indeterminate, checked]);

    return (
      <label
        className={cn(
          'group inline-flex items-start gap-2.5 select-none',
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
          className,
        )}
      >
        <span className="relative inline-flex h-[18px] w-[18px] shrink-0 mt-0.5">
          <input
            ref={setRefs}
            type="checkbox"
            className={SR_ONLY}
            checked={checked}
            disabled={disabled}
            onChange={(e) => {
              onChange?.(e);
              onCheckedChange?.(e.target.checked);
            }}
            {...props}
          />
          <span
            aria-hidden
            className={cn(
              'pointer-events-none inline-flex h-[18px] w-[18px] items-center justify-center rounded-[5px] border transition-colors',
              'peer-focus-visible:ring-2 peer-focus-visible:ring-offset-1',
              boxClassName,
            )}
            style={{
              borderColor:
                checked || indeterminate
                  ? 'var(--color-primary-600)'
                  : 'var(--color-border-primary)',
              backgroundColor:
                checked || indeterminate
                  ? 'var(--color-primary-600)'
                  : 'var(--color-bg-primary)',
              // @ts-expect-error — CSS custom prop for the focus ring offset color
              '--tw-ring-color': 'var(--color-primary-600)',
              '--tw-ring-offset-color': 'var(--color-bg-primary)',
            }}
          >
            {indeterminate ? (
              <Minus className="h-3 w-3 text-white" strokeWidth={3} />
            ) : checked ? (
              <Check className="h-3 w-3 text-white" strokeWidth={3} />
            ) : null}
          </span>
        </span>
        {(label || description) && (
          <span className="flex flex-col gap-0.5 leading-tight">
            {label && (
              <span
                className="text-sm font-medium"
                style={{ color: 'var(--color-text-primary)' }}
              >
                {label}
              </span>
            )}
            {description && (
              <span
                className="text-xs"
                style={{ color: 'var(--color-text-tertiary)' }}
              >
                {description}
              </span>
            )}
          </span>
        )}
      </label>
    );
  },
);
Checkbox.displayName = 'Checkbox';

const Radio = React.forwardRef<HTMLInputElement, BaseProps>(
  (
    {
      label,
      description,
      onCheckedChange,
      onChange,
      className,
      boxClassName,
      disabled,
      checked,
      ...props
    },
    ref,
  ) => (
    <label
      className={cn(
        'group inline-flex items-start gap-2.5 select-none',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        className,
      )}
    >
      <span className="relative inline-flex h-[18px] w-[18px] shrink-0 mt-0.5">
        <input
          ref={ref}
          type="radio"
          className={SR_ONLY}
          checked={checked}
          disabled={disabled}
          onChange={(e) => {
            onChange?.(e);
            onCheckedChange?.(e.target.checked);
          }}
          {...props}
        />
        <span
          aria-hidden
          className={cn(
            'pointer-events-none inline-flex h-[18px] w-[18px] items-center justify-center rounded-full border transition-colors',
            'peer-focus-visible:ring-2 peer-focus-visible:ring-offset-1',
            boxClassName,
          )}
          style={{
            borderColor: checked
              ? 'var(--color-primary-600)'
              : 'var(--color-border-primary)',
            backgroundColor: 'var(--color-bg-primary)',
            // @ts-expect-error — CSS custom prop for the focus ring
            '--tw-ring-color': 'var(--color-primary-600)',
            '--tw-ring-offset-color': 'var(--color-bg-primary)',
          }}
        >
          {checked && (
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: 'var(--color-primary-600)' }}
            />
          )}
        </span>
      </span>
      {(label || description) && (
        <span className="flex flex-col gap-0.5 leading-tight">
          {label && (
            <span
              className="text-sm font-medium"
              style={{ color: 'var(--color-text-primary)' }}
            >
              {label}
            </span>
          )}
          {description && (
            <span
              className="text-xs"
              style={{ color: 'var(--color-text-tertiary)' }}
            >
              {description}
            </span>
          )}
        </span>
      )}
    </label>
  ),
);
Radio.displayName = 'Radio';

export { Checkbox, Radio };
